"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faMagnifyingGlass,
  faSpinner,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import FormInput from "@/components/ui/form-input";
import { CC_SEARCH } from "@/components/case-checking/caseCheckingChrome";
import {
  launchSoderbergPlannrCaseCheck,
  listSoderbergPlannrFirms,
  searchSoderbergPlannrClients,
  type SoderbergPlannrClientRow,
  type SoderbergPlannrFirm,
} from "@/services/api/case-checking";
import { caseCheckAgentName } from "./agenticCaseCheck";

export function SoderbergPlannrNewCaseDialog({
  onClose,
  onCaseLaunched,
  companyId,
}: {
  onClose: () => void;
  onCaseLaunched: (caseId: string) => void;
  companyId?: string;
}) {
  const agentName = caseCheckAgentName(companyId);
  const [firms, setFirms] = useState<SoderbergPlannrFirm[]>([]);
  const [firmKey, setFirmKey] = useState("wellesley");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SoderbergPlannrClientRow[]>([]);
  const [selected, setSelected] = useState<SoderbergPlannrClientRow | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firmsError, setFirmsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const listed = await listSoderbergPlannrFirms();
        if (cancelled) return;
        setFirms(listed);
        setFirmKey((current) =>
          listed.some((firm) => firm.key === current) ? current : listed[0]?.key || current,
        );
      } catch (err) {
        if (!cancelled) {
          setFirmsError(
            err instanceof Error
              ? err.message
              : "Plannr is not configured. Set SODERBERG_PLANNR_PERSONAL_ACCESS_TOKEN on the API.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = async () => {
    setError(null);
    setSelected(null);
    setSearchLoading(true);
    try {
      const result = await searchSoderbergPlannrClients(firmKey, query.trim());
      setResults(result.clients);
      if (result.clients.length === 0) {
        setError("No Plannr clients matched that search.");
      }
    } catch (err) {
      setResults([]);
      setError(err instanceof Error ? err.message : "Could not search Plannr.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleLaunch = async () => {
    if (!selected) {
      setError("Select a Plannr client first.");
      return;
    }
    setError(null);
    setLaunchLoading(true);
    try {
      const launched = await launchSoderbergPlannrCaseCheck({
        firm_key: firmKey,
        plannr_client_uuid: selected.uuid,
        client_name: selected.name,
      });
      onCaseLaunched(launched.case_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the file check.");
    } finally {
      setLaunchLoading(false);
    }
  };

  const busy = searchLoading || launchLoading;

  return (
    <Dialog open onClose={onClose} size="2xl">
      <div className="border-b border-slate-200 dark:border-slate-700">
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <span className="text-lg font-medium text-slate-900 dark:text-white">
              New case from Plannr
            </span>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 dark:hover:bg-slate-700"
              aria-label="Close dialog"
            >
              <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
            </button>
          </div>
        </DialogTitle>
      </div>

      <DialogBody className="flex-1 space-y-4 overflow-y-auto p-6 pt-6">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Search a Wellesley or Prospera client in Plannr. We only read documents — nothing is
          written back to the CRM. {agentName} then runs the file check.
        </p>

        {firmsError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">{firmsError}</p>
          </div>
        ) : null}

        <div className="space-y-2">
          <label
            htmlFor="soderberg-plannr-firm"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Firm
          </label>
          <select
            id="soderberg-plannr-firm"
            data-testid="soderberg-plannr-firm"
            className={clsx(CC_SEARCH, "px-3")}
            value={firmKey}
            onChange={(event) => {
              setFirmKey(event.target.value);
              setResults([]);
              setSelected(null);
            }}
            disabled={busy}
          >
            {(firms.length ? firms : [
              { key: "wellesley", name: "Wellesley" },
              { key: "prospera", name: "Prospera" },
            ]).map((firm) => (
              <option key={firm.key} value={firm.key}>
                {firm.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="soderberg-plannr-search"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Client name
          </label>
          <div className="flex gap-2">
            <FormInput
              id="soderberg-plannr-search"
              data-testid="soderberg-plannr-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Plannr"
              disabled={busy}
              className={clsx("flex-1", CC_SEARCH)}
            />
            <Button
              type="button"
              plain
              onClick={() => void handleSearch()}
              disabled={busy || !query.trim()}
              data-testid="soderberg-plannr-search-button"
              className="shrink-0"
              icon={
                searchLoading ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="h-4 w-4" />
                )
              }
            >
              Search
            </Button>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">{error}</p>
          </div>
        ) : null}

        {results.length > 0 ? (
          <ul className="max-h-56 space-y-1 overflow-y-auto" data-testid="soderberg-plannr-results">
            {results.map((row) => (
              <li key={row.uuid}>
                <button
                  type="button"
                  onClick={() => setSelected(row)}
                  className={clsx(
                    "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm",
                    selected?.uuid === row.uuid
                      ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-100"
                      : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800",
                  )}
                >
                  <span>{row.name}</span>
                  {selected?.uuid === row.uuid ? (
                    <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-indigo-500" />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </DialogBody>

      <div className="border-t border-slate-200 dark:border-slate-700">
        <DialogActions>
          <Button
            type="button"
            color="indigo"
            onClick={() => void handleLaunch()}
            disabled={!selected || launchLoading}
            className="w-full !rounded-xl py-3"
            data-testid="soderberg-plannr-launch"
            icon={
              launchLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
              ) : undefined
            }
          >
            {launchLoading ? "Starting…" : `Start file check with ${agentName}`}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
